import React, { Suspense } from 'react';
import { importRemote } from '@module-federation/utilities';
import { Loader, tokens } from '@hotjar/hotsauce';

import { useCurrentUser } from '@hotjar/user';
import { useCurrentOrganization } from '@hotjar/webapp/hooks/useCurrentOrganization';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import type { RemoteEntryInterface } from '../types';
import { mfeRemoteHost as url, createLogger, createAnalytics } from '../utils';
import { mapCurrentUserToMfe } from '../utils/mapUserData';
import { ErrorBoundary } from '../ErrorBoundary';

// See https://gitlab.com/hotjar/pingpong/engage-insights/-/blob/main/packages/dashboard/webpack.config.js for remote configuration details
const scope = 'engage_insights_dashboard';
const module = 'Dashboard';
const bustRemoteEntryCache = false; // We're using server cache control `cache-control: public, max-age=0, must-revalidate + etag`
const MicroFrontend = React.lazy(() =>
  importRemote({ url, scope, module, bustRemoteEntryCache }),
) as unknown as RemoteEntryInterface;
const logger = createLogger({
  name: 'engageInsights',
  team: 'Engage',
  feature: 'Engage-Insights-Dashboard-MFE',
});
const analytics = createAnalytics();

export function EngageInsights(): JSX.Element {
  const { data: user } = useCurrentUser();
  const { data: organization } = useCurrentOrganization();
  const canAccessBilling = usePermission('subscriptions-manage');
  const mappedUserData = mapCurrentUserToMfe(user, organization, { canAccessBilling });

  return (
    <Suspense
      fallback={
        <div data-testid="mfe-loading" style={{ padding: tokens.spacer.x2 }}>
          <Loader isLoading size="xl" />
        </div>
      }
    >
      <ErrorBoundary logger={logger}>
        <MicroFrontend logger={logger} analytics={analytics} userData={mappedUserData} />
      </ErrorBoundary>
    </Suspense>
  );
}
